import {
  createLogger,
  splitCaptureArgs,
  writeJsonToConsole,
} from "@listen-labs/o11y/logs";
import type {
  CaptureMessageLevel,
  LogMeta,
  Logger,
} from "@listen-labs/o11y/logs";
import type { TagValue } from "@listen-labs/o11y/metrics";

export type { LogMeta, Logger, LogLevel } from "@listen-labs/o11y/logs";

// Shared by server and client components, which can import neither runtime logger (`server-only`
// vs `"use client"`). Each runtime logger registers itself on load; until then records go to the console.
let registered: Logger | undefined;

export const registerIsomorphicLogger = (logger: Logger): void => {
  registered = logger;
};

const fallback = createLogger({ write: writeJsonToConsole });

const createIsomorphicLogger = (bindings: LogMeta): Logger => {
  // Resolved per call, so a child made before registration still reaches the runtime logger.
  const target = (): Logger => {
    const base = registered ?? fallback;
    return Object.keys(bindings).length === 0 ? base : base.child(bindings);
  };
  return {
    debug: (message, ...args) => target().debug(message, ...args),
    info: (message, ...args) => target().info(message, ...args),
    warn: (message, ...args) => target().warn(message, ...args),
    error: (message, ...args) => target().error(message, ...args),
    captureException: (error: unknown, ...args: (LogMeta | TagValue)[]) => {
      const { attributes, tags } = splitCaptureArgs(args);
      target().captureException(error, attributes, ...tags);
    },
    captureMessage: (
      message: string,
      level: CaptureMessageLevel,
      ...args: (LogMeta | TagValue)[]
    ) => {
      const { attributes, tags } = splitCaptureArgs(args);
      target().captureMessage(message, level, attributes, ...tags);
    },
    child: (meta) => createIsomorphicLogger({ ...bindings, ...meta }),
  };
};

/** For modules shared by server and client components. Delegates to the runtime's logger. */
export const logger: Logger = createIsomorphicLogger({});
